import React, { useState } from 'react';
import { IndianRupee, FileText, Printer, Loader2, XCircle, User, Building2, Undo2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useBillingUtils } from '@/hooks/useBillingUtils';
import { usePermission } from '@/hooks/usePermission';
import { InvoiceStatusBadge } from './InvoiceCard';
import { RefundModal } from './RefundModal';
import { cn } from '@/lib/utils';
// Import centralized types
import type { Invoice } from '@/types';

interface PaymentRecordModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoice: Invoice | null;
  onRecordPayment: (amount: number, method: string, meta?: { reference_number?: string; upi_app?: string; bank_name?: string }) => void;
  loading?: boolean;
}

export function PaymentRecordModal({
                                     isOpen,
                                     onClose,
                                     invoice,
                                     onRecordPayment,
                                     loading = false
                                   }: PaymentRecordModalProps) {
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [upiApp, setUpiApp] = useState('');
  const [bankName, setBankName] = useState('');
  const { formatCurrency } = useBillingUtils();

  // Update payment amount when invoice changes
  React.useEffect(() => {
    if (invoice) {
      setPaymentAmount(Number(invoice.balance_due) || 0);
    }
  }, [invoice]);

  const handleSubmit = () => {
    if (!invoice || paymentAmount <= 0) return;
    onRecordPayment(paymentAmount, paymentMethod, {
      reference_number: referenceNumber || undefined,
      upi_app:          upiApp          || undefined,
      bank_name:        bankName        || undefined,
    });
  };

  const handleClose = () => {
    setPaymentAmount(0);
    setPaymentMethod('cash');
    setReferenceNumber('');
    setUpiApp('');
    setBankName('');
    onClose();
  };

  const handleMethodChange = (method: string) => {
    setPaymentMethod(method);
    // Clear method-specific fields when switching
    setReferenceNumber('');
    setUpiApp('');
    setBankName('');
  };

  return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-md">
          <DialogHeader className="border-b pb-4">
            <DialogTitle className="flex items-center gap-2">
              <IndianRupee className="w-5 h-5 text-success" />
              Record Payment
            </DialogTitle>
          </DialogHeader>

          {invoice && (
              <div className="space-y-4">
                {/* Invoice summary */}
                <div className="medical-section p-4 bg-muted/30 rounded-lg">
                  <div className="text-sm space-y-2">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Invoice:</span>
                      <span className="font-medium text-foreground">{invoice.invoice_number}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Patient:</span>
                      <span className="font-medium text-foreground">{invoice.patient_name || '—'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Total Amount:</span>
                      <span className="font-medium text-foreground">{formatCurrency(invoice.total_amount)}</span>
                    </div>
                    {invoice.paid_amount > 0 && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Already Paid:</span>
                          <span className="font-medium text-success">{formatCurrency(invoice.paid_amount)}</span>
                        </div>
                    )}
                    <div className="flex justify-between border-t pt-2">
                      <span className="text-muted-foreground">Balance Due:</span>
                      <span className="font-semibold text-warning">
                        {formatCurrency(invoice.balance_due)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Payment amount */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Payment Amount *
                  </label>
                  <div className="relative">
                    <IndianRupee className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                    <Input
                        type="number"
                        min="0"
                        max={Number(invoice.balance_due)}
                        step="0.01"
                        value={paymentAmount}
                        onChange={(e) => setPaymentAmount(parseFloat(e.target.value) || 0)}
                        placeholder="Enter payment amount"
                        className="pl-10"
                    />
                  </div>
                </div>

                {/* Payment method */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Payment Method
                  </label>
                  <Select value={paymentMethod} onValueChange={handleMethodChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="upi">UPI</SelectItem>
                      <SelectItem value="card">Card</SelectItem>
                      <SelectItem value="netbanking">Bank Transfer</SelectItem>
                      <SelectItem value="cheque">Cheque</SelectItem>
                      <SelectItem value="other">Insurance / Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* UPI app selector — shown only for upi */}
                {paymentMethod === 'upi' && (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          UPI App
                        </label>
                        <Select value={upiApp} onValueChange={setUpiApp}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select app (optional)" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="gpay">Google Pay</SelectItem>
                            <SelectItem value="phonepe">PhonePe</SelectItem>
                            <SelectItem value="paytm">Paytm</SelectItem>
                            <SelectItem value="bhim">BHIM</SelectItem>
                            <SelectItem value="other">Other UPI</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          UPI Transaction ID
                        </label>
                        <Input
                            value={referenceNumber}
                            onChange={(e) => setReferenceNumber(e.target.value)}
                            placeholder="e.g. 123456789012"
                            className="font-mono text-sm"
                        />
                      </div>
                    </div>
                )}

                {/* Cheque details */}
                {paymentMethod === 'cheque' && (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Cheque Number
                        </label>
                        <Input
                            value={referenceNumber}
                            onChange={(e) => setReferenceNumber(e.target.value)}
                            placeholder="Enter cheque number"
                            className="font-mono text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Bank Name
                        </label>
                        <Input
                            value={bankName}
                            onChange={(e) => setBankName(e.target.value)}
                            placeholder="e.g. SBI, HDFC, ICICI"
                        />
                      </div>
                    </div>
                )}

                {/* Bank transfer details */}
                {paymentMethod === 'netbanking' && (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Transaction Reference
                        </label>
                        <Input
                            value={referenceNumber}
                            onChange={(e) => setReferenceNumber(e.target.value)}
                            placeholder="NEFT/RTGS reference number"
                            className="font-mono text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Bank Name
                        </label>
                        <Input
                            value={bankName}
                            onChange={(e) => setBankName(e.target.value)}
                            placeholder="e.g. SBI, HDFC, ICICI"
                        />
                      </div>
                    </div>
                )}

                {/* Card — last 4 digits */}
                {paymentMethod === 'card' && (
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Card Last 4 Digits
                      </label>
                      <Input
                          value={referenceNumber}
                          onChange={(e) => setReferenceNumber(e.target.value.replace(/\D/g, '').slice(0, 4))}
                          placeholder="e.g. 4321"
                          maxLength={4}
                          className="font-mono text-sm w-24"
                      />
                    </div>
                )}

              </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
                variant="outline"
                onClick={handleClose}
                disabled={loading}
            >
              Cancel
            </Button>
            <Button
                onClick={handleSubmit}
                disabled={loading || paymentAmount <= 0 || paymentAmount > (Number(invoice?.balance_due) || 0)}
                className="bg-success hover:bg-success/80 text-success-foreground"
            >
              {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Recording...
                  </>
              ) : (
                  'Record Payment'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
  );
}

interface InvoiceViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoice: Invoice | null;
  onPrint: (invoice: Invoice) => void;
  onPayment?: (invoice: Invoice) => void;
  onRefunded?: () => void;
  showPatientInfo?: boolean;
  variant?: 'patient' | 'clinic';
}

export function InvoiceViewModal({
                                   isOpen,
                                   onClose,
                                   invoice,
                                   onPrint,
                                   onPayment,
                                   onRefunded,
                                   showPatientInfo = true,
                                   variant = 'patient'
                                 }: InvoiceViewModalProps) {
  const { formatCurrency, formatDate } = useBillingUtils();
  const { can } = usePermission();
  const [showRefundModal, setShowRefundModal] = useState(false);

  const isCancelled = invoice?.payment_status === 'cancelled';
  const canAcceptPayment = !isCancelled && invoice && invoice.balance_due > 0 && onPayment;
  // Refunds are allowed on cancelled invoices too — "cancel, then return the money"
  // is the most common real-world refund trigger.
  const canRefund = can('billing:refund') && invoice && Number(invoice.paid_amount) > 0;

  const handleSavePDF = async (invoice: Invoice) => {
    if (!globalThis.electronAPI) return;
    try {
      const dateStr = new Date(invoice.invoice_date).toISOString().split('T')[0];
      const patientNameClean = (invoice.patient_name || 'Patient').replace(/[^a-zA-Z0-9]/g, '_');
      const filename = `Invoice_${invoice.invoice_number}_${patientNameClean}_${dateStr}.pdf`;
      await globalThis.electronAPI.printToPDF({
        paperSize: 'A4',
        margins: { top: 10, bottom: 10, left: 10, right: 10 },
        save: true,
        filename,
        autoOpen: true
      });
    } catch (error) {
      console.error('Failed to save PDF:', error);
    }
  };

  return (
      <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="border-b pb-4">
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Invoice Details
            </DialogTitle>
          </DialogHeader>

          {invoice && (
              <div className="space-y-4">

                {/* Cancelled banner — shown prominently at top */}
                {isCancelled && (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm font-medium">
                      <XCircle className="w-4 h-4 shrink-0" />
                      This invoice has been cancelled. No further payments can be recorded.
                    </div>
                )}

                {/* Clinic Header — uses actual clinic name from API response */}
                <div className="text-center border-b pb-4">
                  <div className="flex items-center justify-center gap-1.5 mb-0.5">
                    <Building2 className="w-4 h-4 text-muted-foreground" />
                    <h3 className="text-xl font-bold text-foreground">
                      {invoice.clinic_name || 'Clinic'}
                    </h3>
                  </div>
                  {invoice.clinic_address && (
                      <p className="text-xs text-muted-foreground">{invoice.clinic_address}</p>
                  )}
                  <p className={cn(
                      "text-lg font-semibold mt-2",
                      isCancelled ? "text-destructive line-through decoration-destructive/60" : "text-primary"
                  )}>
                    {invoice.invoice_number}
                  </p>
                </div>

                {/* Patient & Bill Info */}
                {showPatientInfo && (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="medical-section p-4 bg-muted/30 rounded-lg space-y-1">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Patient</p>
                        <div className="flex items-center gap-1.5">
                          <User className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                          <p className="font-semibold text-foreground text-sm">{invoice.patient_name || '—'}</p>
                        </div>
                        {invoice.patient_phone && (
                            <p className="text-sm text-muted-foreground pl-5">{invoice.patient_phone}</p>
                        )}
                      </div>

                      <div className="medical-section p-4 bg-muted/30 rounded-lg text-right space-y-1">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Bill Info</p>
                        <p className="font-semibold text-foreground text-sm">{formatDate(invoice.invoice_date)}</p>
                        {invoice.doctor_name && (
                            <p className="text-sm text-muted-foreground">{invoice.doctor_name}</p>
                        )}
                      </div>
                    </div>
                )}

                {/* Payment Status */}
                <div className="medical-section p-3 bg-muted/30 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-foreground">Payment Status</span>
                    <InvoiceStatusBadge status={invoice.payment_status} size="md" />
                  </div>
                </div>

                {/* Services */}
                {invoice.items && invoice.items.length > 0 && (
                    <div className="medical-section p-4 bg-muted/30 rounded-lg">
                      <h4 className="text-sm font-medium text-foreground mb-3">Services</h4>
                      <div className="space-y-1">
                        {invoice.items.map((item, index) => (
                            <div
                                key={index}
                                className="flex justify-between items-center text-sm py-2 border-b border-border last:border-0"
                            >
                              <div>
                                <span className="font-medium text-foreground">{item.service_name}</span>
                                {item.quantity > 1 && (
                                    <span className="text-muted-foreground ml-2 text-xs">× {item.quantity}</span>
                                )}
                              </div>
                              <span className="font-medium tabular-nums">
                        {formatCurrency(item.total_price || item.unit_price || 0)}
                      </span>
                            </div>
                        ))}
                      </div>
                    </div>
                )}

                {/* Amount Summary */}
                <div className={cn(
                    "medical-section p-4 rounded-lg space-y-2.5",
                    isCancelled ? "bg-destructive/5 border border-destructive/10" : "bg-muted/30"
                )}>
                  {Number(invoice.discount_amount) > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Subtotal</span>
                        <span className="tabular-nums">{formatCurrency(Number(invoice.subtotal ?? invoice.total_amount))}</span>
                      </div>
                  )}
                  {Number(invoice.discount_amount) > 0 && (
                      <div className="flex justify-between text-sm text-success">
                        <span>Discount</span>
                        <span className="tabular-nums">− {formatCurrency(Number(invoice.discount_amount))}</span>
                      </div>
                  )}
                  {Number(invoice.tax_amount) > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Tax</span>
                        <span className="tabular-nums">+ {formatCurrency(Number(invoice.tax_amount))}</span>
                      </div>
                  )}
                  <div className="flex justify-between text-base border-t pt-2">
                    <span className="font-semibold text-foreground">Total</span>
                    <span className={cn(
                        "font-bold tabular-nums",
                        isCancelled ? "text-destructive line-through" : "text-primary"
                    )}>
                      {formatCurrency(Number(invoice.total_amount))}
                    </span>
                  </div>
                  {!isCancelled && Number(invoice.paid_amount) > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Paid</span>
                        <span className="font-medium text-success tabular-nums">
                          {formatCurrency(Number(invoice.paid_amount))}
                        </span>
                      </div>
                  )}
                  {Number(invoice.refunded_amount) > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Refunded</span>
                        <span className="font-medium text-destructive tabular-nums">
                          {formatCurrency(Number(invoice.refunded_amount))}
                        </span>
                      </div>
                  )}
                  {!isCancelled && Number(invoice.balance_due) > 0 && (
                      <div className="flex justify-between text-sm border-t pt-2">
                        <span className="font-medium text-foreground">Balance Due</span>
                        <span className="font-semibold text-warning tabular-nums">
                          {formatCurrency(Number(invoice.balance_due))}
                        </span>
                      </div>
                  )}
                </div>

              </div>
          )}

          {/* Footer Actions */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" size="sm" onClick={onClose}>
              Close
            </Button>

            {/* Print — allowed even on cancelled invoices for records */}
            <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-1.5"
                onClick={() => invoice && onPrint(invoice)}
            >
              <Printer className="w-3.5 h-3.5" />
              Print
            </Button>

            {/* Save PDF — Electron only */}
            {globalThis.electronAPI && invoice && (
                <Button
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-1.5 text-primary border-primary/40 hover:bg-primary/5"
                    onClick={() => handleSavePDF(invoice)}
                >
                  <FileText className="w-3.5 h-3.5" />
                  Save PDF
                </Button>
            )}

            {/* Refund — shown for paid/partially paid, non-cancelled invoices */}
            {canRefund && (
                <Button
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-1.5 text-destructive border-destructive/30 hover:bg-destructive/10"
                    onClick={() => setShowRefundModal(true)}
                >
                  <Undo2 className="w-3.5 h-3.5" />
                  Refund
                </Button>
            )}

            {/* Record Payment — hidden for cancelled invoices */}
            {canAcceptPayment && (
                <Button
                    size="sm"
                    onClick={() => {
                      onClose();
                      onPayment!(invoice!);
                    }}
                    className="bg-success hover:bg-success/80 text-success-foreground flex items-center gap-1.5"
                >
                  <IndianRupee className="w-3.5 h-3.5" />
                  Record Payment
                </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <RefundModal
          isOpen={showRefundModal}
          onClose={() => setShowRefundModal(false)}
          invoice={invoice}
          onRefunded={onRefunded}
      />
      </>
  );
}